<template>
  <div class="root" :class="rootClass">
    <sp-label v-if="label" for="select" block>{{ label }}</sp-label>
    <sp-quantum-select
      id="select"
      ref="container"
      class="sp-select"
      navigation="flat"
      :hide-search-bar="hideSearchBarAttr"
      :max-results="props.maxResults"
      :fuzzy-search="fuzzySearchAttr"
      :close-on-select="closeOnSelectAttr"
      :class="modifierClass"
      :open="menuOpen"
      :items="items"
      :item-title="itemTitle"
      :item-value="itemValue"
      :selected.prop="model"
      :multiple="multiple"
      :required="props.required"
      :appearance="menuAppearance"
      :hide-no-data="hideNoData"
      :no-data-text="noDataText"
      :wrap="props.wrap"
      :pointer="props.pointer"
      max-height="325px"
      selected-prepend-icon="check-alt-color"
      @update-open="handleMenuUpdateOpen"
      @update-selected="handleUpdateSelected"
      @keydown.down="handleMenuKeyDown"
    >
      <div slot="activator" class="sp-select-activator">
        <slot name="activator">
          <!-- `.attr` seeds the field; selection changes write the live `value` property below. -->
          <sp-text-field
            ref="activatorField"
            class="sp-select-activator__input"
            :inputmode="inputmode"
            :readonly="inputmode === 'none'"
            :value.attr="textValue"
            :disabled="disabledAttr"
            :placeholder="placeholder"
            :loading="loading"
            :density="activatorDensity"
            :variant="activatorVariant"
            propagate-append-click
            @click.prevent.stop="menuOpen = !menuOpen"
            @input="handleTextInput"
          >
            <div slot="prepend">
              <slot name="prepend" @click="handlePrependClick"></slot>
            </div>
            <div slot="append">
              <slot name="append" @click="handleAppendClick">
                <sp-caret v-if="caretVisible" :active="menuOpen" :variant="props.caretVariant"></sp-caret>
              </slot>
            </div>
          </sp-text-field>
        </slot>
      </div>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { onClickOutside } from "@vueuse/core";
import { computed, nextTick, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as selectProps } from "./props.js";

// sp-select is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(formAssociatedHost());

const emit = defineEmits(["input", "text-input", "click-append", "click-prepend", "update-menu"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(selectProps);

const container = useTemplateRef("container");

const loading = computed(() => toBoolean(props.loading));
const returnObject = computed(() => toBoolean(props.returnObject));
const multiple = computed(() => toBoolean(props.multiple));
const searchable = computed(() => toBoolean(props.searchable));
// Bind null (not false) when searchable so the inner quantum-select never renders
// a stray `hide-search-bar="false"` — same pattern as `disabledAttr` below.
const hideSearchBarAttr = computed(() => !searchable.value || null);
// Same null-not-false pattern for the fuzzy in-list matching opt-in.
const fuzzySearchAttr = computed(() => toBoolean(props.fuzzySearch) || null);
const keepOpenOnSelect = computed(() => toBoolean(props.keepOpenOnSelect));
// Same null-not-false attribute pattern: drop `close-on-select` from the inner
// quantum-select when the menu should stay open across selections.
const closeOnSelectAttr = computed(() => !keepOpenOnSelect.value || null);
// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });
// Bind null (not false) so the activator field never renders a stray `disabled="false"`,
// alongside the registration-level normalization.
const disabledAttr = computed(() => disabled.value || null);

const items = computed(() => {
  const items = [];

  if (props.prompt) {
    items.push({ [props.itemTitle]: props.prompt, [props.itemValue]: null });
  }

  return [...items, ...props.items];
});

const menuOpen = ref(toBoolean(props.menu));
watch(menuOpen, (value) => emit("update-menu", value));

function handleMenuUpdateOpen({ detail }) {
  const [value] = detail;
  menuOpen.value = value;
}

function handleMenuKeyDown() {
  if (!menuOpen.value) {
    menuOpen.value = true;
  }
}

const modifierClass = computed(() => ({
  "--disabled": disabled.value,
  "--is-active": menuOpen.value,
}));

const textAppearance = computed(() => props.appearance === "text");

// The text activator sizes itself to the selected label, so the dropdown cannot take its
// width from the activator — that is what the inline popup sizing already does.
const menuAppearance = computed(() => (textAppearance.value ? "inline" : props.appearance));

// The borderless compact activator is what makes the text appearance quiet, so it supplies
// both as defaults. An explicitly chosen variant or density still wins.
const activatorVariant = computed(() =>
  textAppearance.value && props.variant === "default" ? "plain" : props.variant,
);
const activatorDensity = computed(() =>
  textAppearance.value && props.density === "default" ? "compact" : props.density,
);

const rootClass = computed(() => ({
  "--wrap": toBoolean(props.wrap),
  "--is-active": menuOpen.value,
  "--appearance-text": textAppearance.value,
}));

// The `value` attribute seeds the selection and names what a form reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value);

const model = ref(initialModel());

function initialModel(value = defaultValue.value) {
  return promptNormalized(toArray(value ?? []));
}

// An empty selection reads as the prompt row, wherever the write came from. Normalizing to the same
// array when there is nothing to do leaves the ref untouched, so this settles in one pass.
watch(model, () => {
  model.value = promptNormalized(model.value);
});

function promptNormalized(entries) {
  return entries.length === 0 && props.prompt ? [null] : entries;
}

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value;
    model.value = toArray(value ?? []);
  },
);

// The selection reads as the shape the `input` event carries: an array while `multiple`, the
// single entry otherwise.
const selection = computed(() => {
  const entries = returnObject.value ? model.value.map(findItem) : model.value;
  return multiple.value ? entries : entries?.[0];
});

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => selection.value,
    set: setSelection,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// An item carrying "" is reachable only through the menu — matching "" against the items would
// race async item loads.
function setSelection(value) {
  if (isNoSelection(value)) {
    model.value = [];
    return;
  }

  // A return-object consumer hands the item object back; the model stores its value. Without
  // `return-object` an object is the item's own value, so unwrapping it would lose the selection.
  model.value = toArray(value).map((entry) =>
    returnObject.value && isObjectLike(entry) ? getItemValue(entry) : entry,
  );
}

// A native <select multiple> submits one entry per selected option, so the multiple case hands
// setFormValue a FormData rather than a single string.
const { mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();

const formValue = computed(() => {
  const values = model.value.filter((entry) => entry !== null && entry !== undefined);

  if (!multiple.value) {
    return String(values[0] ?? "");
  }

  if (!props.name) {
    return null;
  }

  const data = new FormData();
  values.forEach((entry) => data.append(props.name, String(entry)));

  return data;
});

mirrorFormValue(formValue);

// A required select is satisfied only once something other than the prompt is selected.
const isRequired = computed(() => isPresent(props.required));
const hasSelection = computed(() => model.value.some((entry) => entry !== null && entry !== undefined && entry !== ""));

mirrorValueMissing(
  () => isRequired.value && !hasSelection.value,
  () => t("select_value_missing"),
  container,
);

onFormReset(() => (model.value = initialModel()));

const textValue = computed(() => {
  if (multiple.value) {
    return model.value?.length ? `${model.value.length} selected` : "";
  }

  const item = findItem(model.value[0]);
  const title = getItemTitle(item);

  if (title) {
    const isPrompt = props.prompt && items.value[0] === item;
    const prepend = props.prependSelectedWith && !isPrompt ? props.prependSelectedWith : null;
    return [prepend, title].filter(Boolean).join(" ");
  }

  return undefined;
});

function findItem(value) {
  return items.value.find((item) => String(getItemValue(item)) === String(value));
}

// The activator field pins user-typed text against attribute changes (the dirty value flag), so
// the selection's text is written to the live `value` property — the channel that always adopts.
const activatorField = useTemplateRef("activatorField");

watch([() => model.value[0], textValue], syncActivatorText);

function syncActivatorText([entry, value], [previousEntry]) {
  if (!activatorField.value) {
    return;
  }

  // An undefined title on an unchanged selection is item-list churn (typing filters the list,
  // async items still loading) — never blank the field over that; the title write lands once it
  // resolves. A changed selection always writes, so the field cannot show a stale title.
  if (value === undefined && entry === previousEntry && hasSelection.value) {
    return;
  }

  activatorField.value.value = value ?? "";
}

const hasItems = computed(() => props.items?.length > 0);
const caretVisible = computed(() => hasItems.value);

function getItemTitle(item) {
  return getItemAttribute(item, props.itemTitle);
}

function getItemValue(item) {
  return getItemAttribute(item, props.itemValue);
}

function getItemAttribute(item, attribute) {
  return isObjectLike(item) ? item[attribute] : item;
}

function handlePrependClick() {
  emit("click-prepend");
}

function handleAppendClick() {
  emit("click-append");
}

function handleTextInput({ detail }) {
  const [value] = detail;
  emit("text-input", value);
}

function handleUpdateSelected({ detail }) {
  const [value] = detail;
  model.value = promptNormalized(value);
  // Emitted here, where the user's pick enters, rather than from a watcher on the model: a watcher
  // also fires for attribute syncs, property writes and form resets — which is both wrong (`input`
  // reports user input, as a native control does) and a flush late, long enough for a host's value
  // binding to be re-applied with the value the pick has not reached yet.
  emitBubbling("input", selection.value);

  if (keepOpenOnSelect.value) {
    return;
  }

  // This is required to close the menu after a selection is made and all events have been handled.
  nextTick(closeMenu);
}

onClickOutside(container, closeMenu);

function closeMenu() {
  menuOpen.value = false;
}
</script>

<style>
:host {
  display: block;
  height: fit-content;
  --sp-comp-divider-color: var(--sp-comp-select-divider-color, #ccc);
  --sp-comp-quantum-select-popup-min-height: 0;
  --sp-comp-popup-min-height: 0;
  /* Shield from ancestor --sp-comp-popup-width so the popup matches the activator. */
  --sp-comp-popup-width: 100%;
}
</style>

<style lang="scss" scoped>
.root {
  --sp-comp-popup-min-width: 100% !important;
  --sp-comp-list-item-content-white-space: nowrap;
  --caret-size: var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, var(--sp-sys-icon-size-medium)));

  &.--wrap {
    --sp-comp-quantum-select-popup-width: 0;
    --sp-comp-list-item-content-white-space: normal;
  }

  &.--is-active {
    --sp-comp-text-field-border-color: var(
      --sp-comp-select-active-border-color,
      var(--sp-sys-form-field-focus-border-color, var(--sp-sys-color-primary))
    );
  }

  &.--appearance-text {
    /* `em` throughout, so the control takes the surrounding text's size. */
    --sp-comp-text-field-font-size: 1em;
    --sp-comp-text-field-compact-min-height: var(--sp-comp-select-text-min-height, 1.5rem);
    --caret-size: var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, 1.25em));
    /* The caret is box-centred here, so both of its optical nudges for inline use come off. */
    --sp-comp-caret-offset: 0;
    --sp-comp-caret-active-nudge: 0;

    /* The field hugs the selected label instead of filling its container. */
    --sp-comp-text-field-min-width: 0;
    --sp-comp-text-field-width: auto;
    --sp-comp-text-field-field-sizing: content;

    /* The dropdown grows to its widest entry, which the hugging activator can be narrower than. */
    --sp-comp-popup-width: max-content;

    /* Only the activator takes the surrounding text's size. The rows state a size of their own,
       because `sp-list-item` sizes its title by inheritance wherever the typescale tokens are
       undefined — as they are in admin — which would otherwise shrink the menu with the activator. */
    --sp-comp-list-item-title-font-size: var(--sp-sys-typescale-body-font-size, 1rem);
  }
}

.sp-select-activator__input[inputmode="none"] {
  --sp-comp-text-field-cursor: pointer;
}

.sp-select-list-container {
  position: relative;
  width: 100%;
}

sp-label {
  margin-bottom: var(--sp-comp-select-label-margin-bottom, var(--sp-ref-spacing-3));
  --sp-comp-text-label-short: var(--sp-comp-select-label-font, var(--sp-sys-form-field-label-short));
}

sp-caret {
  --sp-comp-caret-size: var(--caret-size);
  color: var(--sp-comp-select-caret-color, currentColor);
}

[slot="append"],
[slot="prepend"] {
  display: flex;
  align-items: center;
  justify-content: center;
}

[slot="append"] {
  padding-inline-end: var(--sp-comp-select-append-padding-inline-end, 0);
}

.sp-select-list__no-result {
  padding: var(--sp-comp-select-no-result-padding, 1rem);
  color: var(--sp-comp-select-no-result-color, #aaa);
}
</style>
